import { Component } from '@theme/component';

const TILES = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>';

/** @type {Promise<any> | null} */
let leafletPromise = null;

/**
 * Loads the vendored Leaflet once per page, stylesheet and script together.
 *
 * @param {string} js
 * @param {string} css
 * @returns {Promise<any>} window.L
 */
function loadLeaflet(js, css) {
  if (leafletPromise) return leafletPromise;

  leafletPromise = new Promise((resolve, reject) => {
    if (window.L) {
      resolve(window.L);
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = css;
    document.head.append(link);

    const script = document.createElement('script');
    script.src = js;
    script.async = true;
    script.onload = () => resolve(window.L);
    script.onerror = () => {
      leafletPromise = null;
      reject(new Error('Leaflet failed to load'));
    };
    document.head.append(script);
  });

  return leafletPromise;
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  const span = document.createElement('span');
  span.textContent = value;
  return span.innerHTML;
}

/**
 * The store map under the store grid. Waits until it is about to scroll into
 * view before loading Leaflet, then draws one marker per store and fits them all.
 *
 * @typedef {{ name: string, address: string, url: string, directions: string, lat: number | string, lng: number | string }} Store
 * @typedef {{ canvas: HTMLElement, pin: HTMLTemplateElement, data: HTMLScriptElement }} Refs
 *
 * @extends {Component<Refs>}
 */
class MmStoreMap extends Component {
  requiredRefs = ['canvas', 'pin', 'data'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {any} */
  #map = null;

  connectedCallback() {
    super.connectedCallback();

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#observer = null;
        this.#draw();
      },
      { rootMargin: '400px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#map?.remove();
    this.#map = null;
  }

  async #draw() {
    /** @type {Store[]} */
    let stores = [];
    try {
      stores = JSON.parse(this.refs.data.textContent || '[]');
    } catch {
      return;
    }

    const points = stores
      .map((store) => ({ ...store, lat: Number.parseFloat(String(store.lat)), lng: Number.parseFloat(String(store.lng)) }))
      .filter((store) => Number.isFinite(store.lat) && Number.isFinite(store.lng));
    if (!points.length) return;

    const { leafletJs, leafletCss } = this.dataset;
    if (!leafletJs || !leafletCss) return;

    let L;
    try {
      L = await loadLeaflet(leafletJs, leafletCss);
    } catch {
      return;
    }
    if (!this.isConnected || this.#map) return;

    const touch = window.matchMedia('(pointer: coarse)').matches;
    const map = L.map(this.refs.canvas, {
      scrollWheelZoom: false,
      // One finger scrolls the page on a phone; the map moves with the zoom
      // buttons or a pinch, which never fights the page scroll.
      dragging: !touch,
      zoomControl: false,
    });
    this.#map = map;

    L.control
      .zoom({
        zoomInTitle: this.dataset.textZoomIn,
        zoomOutTitle: this.dataset.textZoomOut,
      })
      .addTo(map);

    L.tileLayer(TILES, { attribution: ATTRIBUTION, maxZoom: 19 }).addTo(map);
    map.attributionControl.setPrefix(false);

    const icon = L.divIcon({
      className: 'mm-store-map__marker',
      html: this.refs.pin.innerHTML,
      iconSize: [36, 36],
      iconAnchor: [18, 34],
      popupAnchor: [0, -30],
    });

    const directions = escapeHtml(this.dataset.textDirections || '');
    const view = escapeHtml(this.dataset.textStore || '');

    for (const store of points) {
      const links = [];
      const route = store.directions || `https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}`;
      links.push(
        `<a class="mm-store-map__link" href="${escapeHtml(route)}" target="_blank" rel="noopener noreferrer">${directions}</a>`
      );
      if (store.url) {
        links.push(`<a class="mm-store-map__link" href="${escapeHtml(store.url)}">${view}</a>`);
      }

      const popup = [
        `<p class="mm-store-map__name">${escapeHtml(store.name || '')}</p>`,
        store.address ? `<p class="mm-store-map__address">${escapeHtml(store.address)}</p>` : '',
        `<div class="mm-store-map__links">${links.join('')}</div>`,
      ].join('');

      L.marker([store.lat, store.lng], { icon, title: store.name, alt: store.name, riseOnHover: true })
        .addTo(map)
        .bindPopup(popup, { maxWidth: 280 });
    }

    const bounds = L.latLngBounds(points.map((store) => [store.lat, store.lng]));
    map.fitBounds(bounds, { padding: [48, 48], maxZoom: 15 });
  }
}

if (!customElements.get('mm-store-map')) {
  customElements.define('mm-store-map', MmStoreMap);
}
